import { BRAND, RISE_TINTS, VISMA } from '../../styles/tokens';

/**
 * The Grow logo, Rise v4 (owner's final pick "O4", 2026-11-03): three of Visma's equilateral
 * glyphs in the 2 : 3 : 4 ratio standing on the wordmark's baseline, the big one 1.25 x the cap
 * height, each overlapping the one before by 35% and standing in front of it. No knockout and no
 * masks: depth comes from three tints, back to front (RISE_TINTS: light purples on light, white
 * at 40/67/100% on black, 45/72/100% on purple). Same geometry as rise4.py (scratchpad logo2).
 * Never a gradient inside it. public/favicon.svg, the PNG icons and the email logos match it.
 */

/** The full logo's 64-high box (the public SVGs use it, with clear space). */
export const GROW_LOGO_VIEWBOX = '0 0 246.58 64';

/** The Rise in the logo: small, medium, big, each drawn over the one before. */
export const GROW_LOGO_MARK: readonly string[] = ['M2 58L30.07 58L16.03 33.69Z', 'M20.24 58L62.34 58L41.29 21.54Z', 'M47.61 58L103.74 58L75.68 9.39Z'];

/** "Grow" in Visma Display Medium, as outlines (no Visma font is loaded), placed on the Rise's baseline. */
export const GROW_WORDMARK_TRANSFORM = 'translate(118.61 58.00) scale(0.5636)';

export const GROW_WORDMARK_PATH = 'M33.40 1.40Q23.50 1.40 16.55-3.10Q9.60-7.60 6.10-15.70Q2.60-23.80 2.60-34.50L2.60-34.50Q2.60-45.20 6.20-53.30Q9.80-61.40 16.85-65.90Q23.90-70.40 33.80-70.40L33.80-70.40Q46-70.40 53.65-64Q61.30-57.60 63.40-46.20L63.40-46.20L53.60-46.20Q51.80-54.10 46.90-58.45Q42-62.80 33.80-62.80L33.80-62.80Q23.10-62.80 17.90-54.90Q12.70-47 12.70-34.50L12.70-34.50Q12.70-22.10 18-14.15Q23.30-6.20 33.90-6.20L33.90-6.20Q43.10-6.20 48.10-12.10Q53.10-18 53.60-27.10L53.60-27.10L30.70-27.10L30.70-34.80L63.70-34.80L63.70 0L55.80 0L55-9.60L54.60-9.60Q51.40-4.30 46.05-1.45Q40.70 1.40 33.40 1.40L33.40 1.40ZM72.80 0L72.80-48.60L81.50-48.60L81.50-41.10L82-41.10Q83.90-44.60 87.60-46.60Q91.30-48.60 95.80-48.60L95.80-48.60L97-48.60L97-40.50L95.70-40.50Q89-40.40 85.55-36.20Q82.10-32 82.10-24.80L82.10-24.80L82.10 0L72.80 0ZM120.90 1.40Q110.30 1.40 104.25-5.55Q98.20-12.50 98.20-24.30L98.20-24.30Q98.20-36.10 104.30-43.05Q110.40-50 120.90-50L120.90-50Q127.80-50 132.90-46.85Q138-43.70 140.75-37.90Q143.50-32.10 143.50-24.30L143.50-24.30Q143.50-12.50 137.45-5.55Q131.40 1.40 120.90 1.40L120.90 1.40ZM120.90-5.80Q127.20-5.80 130.70-10.75Q134.20-15.70 134.20-24.30L134.20-24.30Q134.20-33 130.75-37.90Q127.30-42.80 120.90-42.80L120.90-42.80Q114.50-42.80 111-37.90Q107.50-33 107.50-24.30L107.50-24.30Q107.50-15.70 111-10.75Q114.50-5.80 120.90-5.80L120.90-5.80ZM144.50-48.60L154.40-48.60L165.60-6.60L166.30-6.60L176.80-48.60L191.40-48.60L201.90-6.60L202.60-6.60L213.80-48.60L223.50-48.60L209.80 0L195 0L184.30-41.10L183.70-41.10L172.90 0L158.10 0L144.50-48.60Z';

/** The mark alone, centred in a 64 x 64 box (small, medium, big). */
export const GROW_MARK: readonly string[] = ['M4.48 45.15L19.66 45.15L12.07 32Z', 'M14.35 45.15L37.12 45.15L25.74 25.43Z', 'M29.15 45.15L59.52 45.15L44.34 18.85Z'];

/** The big triangle of GROW_MARK: the outline the 404 draws where it is missing. */
export const GROW_MARK_BIG_OUTLINE = 'M29.15 45.15L59.52 45.15L44.34 18.85Z';

/** The mark inside the 64 x 64 app icon tile (46 wide), as in public/favicon.svg. */
export const GROW_ICON_MARK: readonly string[] = ['M9 43.99L21.69 43.99L15.34 33Z', 'M17.25 43.99L36.28 43.99L26.77 27.51Z', 'M29.62 43.99L55 43.99L42.31 22.01Z'];
/** The box the components crop to: the big triangle's tip to just under the baseline. */
const LOGO_CROP = { y: 7, h: 53, w: 246.58 };

/**
 * The mark alone. `tile` puts it on a purple rounded square, the app icon (white tints).
 * Without a tile: the light tints, or `tone` "dark" for black. `color` draws it in one colour.
 */
export function GrowMark({ size = 24, tile = false, tone = 'light', color, className }: {
  size?: number; tile?: boolean; tone?: 'light' | 'dark'; color?: string; className?: string;
}) {
  const parts = tile ? GROW_ICON_MARK : GROW_MARK;
  const tints = tile ? RISE_TINTS.purple : tone === 'dark' ? RISE_TINTS.dark : RISE_TINTS.light;
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 64 64" aria-hidden="true" focusable="false">
      {tile && <rect width="64" height="64" rx="14" fill={BRAND} />}
      {parts.map((d, i) => <path key={d} d={d} fill={color || tints[i]} />)}
    </svg>
  );
}

/**
 * The mark and the wordmark. `tone` "dark" is for light backgrounds (purple tints, black
 * wordmark); "light" is white, for black; "purple" is white on purple or the Amplify gradient.
 */
export function GrowLogo({ height = 28, tone = 'dark', label = 'Grow', className }: {
  height?: number; tone?: 'dark' | 'light' | 'purple'; label?: string; className?: string;
}) {
  const tints = tone === 'light' ? RISE_TINTS.dark : tone === 'purple' ? RISE_TINTS.purple : RISE_TINTS.light;
  const word = tone === 'dark' ? VISMA.black : VISMA.white;
  return (
    <svg
      className={className}
      height={height}
      width={Math.round((height * LOGO_CROP.w) / LOGO_CROP.h)}
      viewBox={`0 ${LOGO_CROP.y} ${LOGO_CROP.w} ${LOGO_CROP.h}`}
      role="img"
      aria-label={label}
      focusable="false"
    >
      {GROW_LOGO_MARK.map((d, i) => <path key={d} d={d} fill={tints[i]} />)}
      <path d={GROW_WORDMARK_PATH} transform={GROW_WORDMARK_TRANSFORM} fill={word} />
    </svg>
  );
}
